import { useState, useCallback } from "react";

import { DollarSign, Check, Trash2, ExternalLink } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

import { cn } from "@/modern/utils";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys.jsx";

import { useFileDetailModal } from "../../../context/FileDetailModalContext";
import { SearchTextLink } from "../SearchTextLink";
import { ExtractedBudget } from "../types";

interface BudgetExtractionResultsProps {
  budgets: ExtractedBudget[];
}

interface BudgetCardState {
  dismissed: boolean;
  opened: boolean;
}

/**
 * Maps extracted budget keys (from the AI API) to TaskBudget model field names.
 * The API returns snake_case keys; the model uses camelCase field names.
 */
const BUDGET_FIELD_MAP: Record<string, string> = {
  contract_amount: "contractAmount",
  change_orders: "changeOrders",
  labor_estimated: "laborEstimated",
  labor_actual: "laborActual",
  materials_estimated: "materialsEstimated",
  materials_actual: "materialsActual",
  subs_estimated: "subsEstimated",
  subs_actual: "subsActual",
  rentals_estimated: "rentalsEstimated",
  rentals_actual: "rentalsActual",
  miscellaneous_estimated: "miscellaneousEstimated",
  miscellaneous_actual: "miscellaneousActual",
  days_estimated: "daysEstimated",
  description: "description",
};

/** Keys to exclude from the display card (internal / non-displayable). */
const HIDDEN_KEYS = new Set(["search_text"]);

/**
 * Formats a raw key string into a human-readable label.
 * e.g. "contract_amount" => "Contract Amount"
 */
const formatLabel = (key: string): string => {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const BudgetExtractionResults = ({ budgets }: BudgetExtractionResultsProps) => {
  const { project, entityRecord, editorHandler } = useFileDetailModal();

  const [cardStates, setCardStates] = useState<Record<number, BudgetCardState>>(() => {
    const initial: Record<number, BudgetCardState> = {};
    budgets.forEach((_, index) => {
      initial[index] = { dismissed: false, opened: false };
    });
    return initial;
  });

  const updateCardState = useCallback((index: number, update: Partial<BudgetCardState>) => {
    setCardStates((prev) => ({
      ...prev,
      [index]: { ...prev[index], ...update },
    }));
  }, []);

  const handleOpenBudgetEditor = useCallback((budget: ExtractedBudget, index: number) => {
    if (!project || !editorHandler) return;

    const taskBudgetStore = project.taskBudgetStore;
    if (!taskBudgetStore) return;

    const budgetData: Record<string, any> = {};
    for (const [apiKey, value] of Object.entries(budget)) {
      if (HIDDEN_KEYS.has(apiKey)) continue;
      const modelField = BUDGET_FIELD_MAP[apiKey];
      if (modelField) {
        if (value == null) {
          budgetData[modelField] = modelField === "description" ? "" : 0;
        } else {
          const numVal = parseFloat(value);
          budgetData[modelField] = isNaN(numVal) ? value : numVal;
        }
      }
    }

    const newRecord = taskBudgetStore.createRecord(budgetData);
    editorHandler.openEditor(EDITOR_KEYS.budgetEditor, newRecord, {
      budget: newRecord,
      onBudgetCreated: (budgetId: number | string) => {
        if (entityRecord) {
          (entityRecord as any).budgetId = budgetId;
        }
      },
    });
    updateCardState(index, { opened: true });
  }, [project, editorHandler, updateCardState, entityRecord]);

  const handleDismiss = useCallback((index: number) => {
    updateCardState(index, { dismissed: true });
  }, [updateCardState]);

  const getDisplayEntries = (budget: ExtractedBudget): [string, string][] => {
    return Object.entries(budget).filter(([key]) => !HIDDEN_KEYS.has(key));
  };

  const visibleBudgets = budgets.filter((_, index) => !cardStates[index]?.dismissed);

  if (visibleBudgets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <p className="text-sm text-muted-foreground">All budget cards have been dismissed.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      <AnimatePresence>
        {budgets.map((budget, index) => {
          const state = cardStates[index];
          if (state?.dismissed) return null;

          const entries = getDisplayEntries(budget);

          return (
            <motion.div
              key={index}
              layout
              initial={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className={cn(
                "border border-border rounded-swivu-base overflow-hidden",
                state?.opened && "border-primary/20 bg-primary/[0.03]"
              )}
            >
              <div className="p-3">
                {/* Header with actions */}
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-1.5">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">
                      Budget {budgets.length > 1 ? `#${index + 1}` : ""}
                    </span>
                  </div>

                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      type="button"
                      title={state?.opened ? "Open in Budget Editor again" : "Open in Budget Editor"}
                      onClick={() => handleOpenBudgetEditor(budget, index)}
                      className={cn(
                        "relative flex items-center justify-center w-7 h-7 rounded-md transition-all duration-150",
                        "hover:bg-primary/10 hover:text-primary active:bg-primary/20",
                        state?.opened && "text-primary"
                      )}
                    >
                      {state?.opened ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        <ExternalLink className="w-3.5 h-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      title="Dismiss"
                      onClick={() => handleDismiss(index)}
                      className="relative flex items-center justify-center w-7 h-7 rounded-md transition-all duration-150 hover:bg-destructive/10 hover:text-destructive active:bg-destructive/20"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Key-value pairs */}
                <div className="flex flex-col gap-1">
                  {entries.map(([key, value]) => (
                    <div key={key} className="flex items-baseline justify-between gap-2 text-sm">
                      <span className="text-muted-foreground shrink-0">{formatLabel(key)}</span>
                      <span className="text-foreground font-medium text-right truncate">{value}</span>
                    </div>
                  ))}
                </div>

                {/* Search text link */}
                {budget.search_text && (
                  <div className="mt-2">
                    <SearchTextLink searchText={budget.search_text} />
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};
